import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { IBase, IClient, ITown } from 'src/app/models';
import { CommunicationUserType, DriverSuggestionType, ExpeditionStatus, InputType, UserRole } from 'src/app/models/enums';
import { SuggestionPriority } from 'src/app/models/enums/suggestionPriority';
import { SuggestionStatus } from 'src/app/models/enums/suggestionStatus';
import { SuggestionType } from 'src/app/models/enums/suggestionType';
import { StorageService, TripsService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import {
  ISuggestionsFilters,
  deserializeSuggestionsFilters,
  parseSuggestionsFiltersFromStorage,
} from 'src/app/filters';

@Component({
  selector: 'app-suggestion-filter',
  templateUrl: './suggestion-filter.component.html',
  styleUrl: './suggestion-filter.component.scss',
  standalone: false
})
export class SuggestionFilterComponent implements OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;
  private servicesLoadedForTownsKey: string | null = null;
  private initialized = false;
  public suggestionStatus: any[] = [];
  public suggestionTypes: any[] = [];
  public suggestionPriorities: any[] = [];
  public SuggestionStatus = SuggestionStatus;
  public SuggestionPriority = SuggestionPriority;
  public SuggestionTypes = SuggestionType;
  public UserRoles = UserRole;
  public closedStatusSelected = false;

  public adminRole: UserRole;
  public clients: IClient[] = [];
  public selectedValuesClients: any[] = [];

  public towns: unknown[] = [];
  public selectedValuesTowns: any[] = [];

  public servicesPage = 1;
  public servicesSearchValue = '';
  public servicesElement: any = {
    id: 'suggestionServices',
    value: [],
    edit: {
      editable: true,
      inputType: InputType.MULTISELECT_SCROLL,
      values: [],
      valueToChange: 'services',
      customSearchEnabled: true,
      placeholder: '-'
    }
  };

  public images = images;
  public TooltipPosition = TooltipPosition;

  public filterSelected = 'all';
  public filters: any[] = [];

  @Output() filter = new EventEmitter();
  @Output() closeFilter = new EventEmitter();

  @Input() componentOpen: string;
  @Input() isFilterOpen: boolean;
  @Input() communicationUserType: CommunicationUserType;

  @ViewChild('select') select: ElementRef;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    public tripsService: TripsService,
    public utilsService: UtilsService,
    public usersService: UsersService,
    public storageService: StorageService,
    public serviceInfoService: ServiceInfoService,
    private activatedRoute: ActivatedRoute,
  ) {
    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById('filterContainer')!;
      const componentOpen = document.getElementById(this.componentOpen)!;
      const close = utilsService.closeCustomModals(e, modal, componentOpen);
      if (close) {
        if (this.select && this.select.nativeElement) this.select.nativeElement.checked = false;
        this.closeFilter.emit();
      }
    });
  }

  async ngOnChanges() {
    if (this.isFilterOpen && !this.initialized) {
      this.initialized = true;
      this.adminRole = await this.storageService.getRole();

      this.suggestionTypes = [];
      if (this.communicationUserType === CommunicationUserType.USERS) {
        (Object.keys(SuggestionType) as Array<keyof typeof SuggestionType>).map(
          (suggestionType: keyof typeof SuggestionType) => {
            this.suggestionTypes.push({ value: suggestionType, checked: false });
          },
        );
      } else {
        (Object.keys(DriverSuggestionType) as Array<keyof typeof DriverSuggestionType>).map(
          (driverSuggestionType: keyof typeof DriverSuggestionType) => {
            this.suggestionTypes.push({ value: driverSuggestionType, checked: false });
          },
        );
      }
      this.suggestionStatus = [];
      (Object.keys(SuggestionStatus) as Array<keyof typeof SuggestionStatus>).map(
        (suggestionStatus: keyof typeof SuggestionStatus) => {
          this.suggestionStatus.push({ value: suggestionStatus, checked: false });
        },
      );
      this.suggestionPriorities = [];
      (Object.keys(SuggestionPriority) as Array<keyof typeof SuggestionPriority>).map(
        (suggestionPriority: keyof typeof SuggestionPriority) => {
          this.suggestionPriorities.push({ value: suggestionPriority, checked: false });
        },
      );

      const { clients, towns } = await this.usersService.getPossibleClientTowns();

      this.clients = [];
      clients.map((client: IClient) => {
        this.clients.push({
          id: client.id,
          code: client.code,
          name: client.name,
        });
      });

      this.towns = [];
      towns.map((town: ITown) => {
        this.towns.push({ id: town.id, code: town.name, name: town.name });
      });

      await this.initFilters();
    }
  }

  async loadServices(showMore = false) {
    const towns = this.selectedValuesTowns.length > 0
      ? this.selectedValuesTowns.map((t: any) => ({ id: t.id }))
      : (this.towns as any[]).map((t: any) => ({ id: t.id }));
    if (towns.length === 0) {
      this.servicesElement.edit.values = [];
      return;
    }
    const result = await this.serviceInfoService.getServiceInfosSearch(10, this.servicesPage, towns as ITown[], this.servicesSearchValue || undefined);
    const content = (result as any).content || result;
    const newValues = (content as any[]).map((s: any) => ({
      id: s.id,
      value: { id: s.id, tag: s.tag, name: s.name },
      label: s.name ? `${s.tag}: ${s.name}` : s.tag
    }));
    this.servicesElement.edit.values = showMore
      ? [...this.servicesElement.edit.values, ...newValues]
      : newValues;
  }

  async onTownsClose() {
    this.servicesElement.value = [];
    this.servicesElement.edit.values = [];
    this.servicesPage = 1;
    this.servicesSearchValue = '';
    await this.loadServices();
  }

  onServicesChange(value: any) {
    this.servicesElement.value = value;
  }

  async onScroll(event: Select2ScrollEvent) {
    if (event.way === 'down') {
      this.servicesPage += 1;
      await this.loadServices(true);
    }
  }

  async onSearch(event: Select2SearchEvent) {
    this.servicesPage = 1;
    this.servicesSearchValue = event.search;
    await this.loadServices();
  }

  async initFilters() {
    const filters = (await this.resolveCurrentFilters()) ?? {
      selectedClients: [],
      selectedTowns: [],
      selectedServices: [],
      selectedTypes: [],
      selectedStatus: [],
      selectedPriorities: [],
    };
    this.applyFiltersToView(filters);

    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(async () => {
      const next = await this.resolveCurrentFilters();
      if (next) this.applyFiltersToView(next);
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  private async resolveCurrentFilters(): Promise<ISuggestionsFilters | null> {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const storageFilters = parseSuggestionsFiltersFromStorage(await this.storageService.getSuggestionsFilters());
    if (params.keys.length > 0) return deserializeSuggestionsFilters(params, storageFilters ?? undefined);
    return storageFilters;
  }

  private applyFiltersToView(selectedFilters: ISuggestionsFilters): void {
    this.selectedValuesTowns = this.towns.filter((town: any) => selectedFilters.selectedTowns.includes(town.id));

    if (selectedFilters.selectedClients) {
      this.selectedValuesClients = this.clients.filter((client: any) => selectedFilters.selectedClients.includes(client.id));
    }

    this.servicesElement.value = selectedFilters.selectedServices || [];

    this.suggestionStatus.map((status: any) => {
      status.checked = selectedFilters.selectedStatus.some((s: any) => s.toLowerCase() === status.value.toLowerCase());
    });

    this.suggestionPriorities.map((priority: any) => {
      priority.checked = selectedFilters.selectedPriorities.some((p: any) => p.toLowerCase() === priority.value.toLowerCase());
    });

    this.suggestionTypes.map((type: any) => {
      type.checked = selectedFilters.selectedTypes.some((t: any) => t.toLowerCase() === type.value.toLowerCase());
    });

    // Avoid refetching /service-infos/filter on every URL change — only reload when
    // the towns scope actually changed (or this is the first load).
    const townsKey = this.selectedValuesTowns.length > 0
      ? this.selectedValuesTowns.map((t: IBase) => t.id).sort().join(',')
      : '<all>';
    if (townsKey !== this.servicesLoadedForTownsKey) {
      this.servicesLoadedForTownsKey = townsKey;
      this.loadServices();
    }
  }

  changeFilter(event: any) {
    this.filterSelected = event.target.value;
  };

  selectAll(event: any) {
    this.suggestionStatus.map(
      (status: any) => (status.checked = event.currentTarget.checked),
    );
    this.suggestionPriorities.map(
      (status: any) => (status.checked = event.currentTarget.checked),
    );
    this.suggestionTypes.map(
      (type: any) => (type.checked = event.currentTarget.checked),
    );
    this.towns.map((town: any) => this.selectedValuesTowns.push({id: town.id, name: town.name}));
    this.clients.map((client: IClient) => this.selectedValuesClients.push(client));
  };

  clearSelection() {
    this.suggestionPriorities.map((priority: any) => (priority.checked = false));
    this.suggestionStatus.map((status: any) => (status.checked = false));
    this.suggestionTypes.map((type: any) => (type.checked = false));
    this.selectedValuesTowns = [];
    this.selectedValuesClients = [];
    this.servicesElement.value = [];
    this.servicesElement.edit.values = [];
    this.servicesPage = 1;
    this.servicesSearchValue = '';
    this.select.nativeElement.checked = false;
    this.loadServices();
  };

  async onSubmit() {
    const selectedClients: any[] = [];
    this.selectedValuesClients.forEach((client: IBase) => {
      selectedClients.push(client.id);
    });

    const selectedTowns: any[] = [];
    this.selectedValuesTowns.forEach((town: IBase) => {
      selectedTowns.push(town.id);
    });

    const selectedServices: any[] = (this.servicesElement.value as any[]).map((s: any) => ({
      id: s.id,
      tag: s.tag,
      name: s.name
    }));

    const selectedTypes: string[] = [];
    this.suggestionTypes.forEach((type: any) => {
      type.checked && selectedTypes.push(type.value);
    });

    const selectedPriorities: string[] = [];
    this.suggestionPriorities.forEach((priority: any) => {
      priority.checked && selectedPriorities.push(priority.value);
    });

    const selectedStatus = [];
    if (this.closedStatusSelected) selectedStatus.push(ExpeditionStatus.CLOSED);
    else {
      this.suggestionStatus.forEach((status: any) => {
        if (status.checked) selectedStatus.push(status.value) ;
      });
    }

    this.storageService.setSuggestionsFilters(JSON.stringify({
      selectedClients,
      selectedTowns,
      selectedServices,
      selectedTypes,
      selectedStatus,
      selectedPriorities
    }));

    this.filter.emit({selectedClients, selectedTowns, selectedServices, selectedTypes, selectedStatus, selectedPriorities});
  };
}
